import type { ChatMessage, Chat as ChatType } from './chat.types';
import type { HTMLAttributes } from 'react';

import {
  ChatRemoveIcon,
  Heading,
  PrimaryButton,
  SubHeading,
  classNames,
} from '@movingimage-evp/mi-ui-component-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MessageForm } from './message-form';
import { MessagesList } from './messages-list';
import { NicknameForm } from './nickname-form';
import { ThreadView } from './thread-view';

import styles from './chat.module.css';

type Props = {
  chat: ChatType;
  anonymous?: boolean;
  hasSecurityPolicy?: boolean;
  moderatorId?: string | null;
  readOnly?: boolean;
  onNicknameSubmit: (nickname: string) => void;
} & HTMLAttributes<HTMLDivElement>;

export function Chat({ chat, anonymous, hasSecurityPolicy, moderatorId, readOnly, onNicknameSubmit, ...props }: Props) {
  const { t } = useTranslation();
  const [messageValue, setMessageValue] = useState('');
  const [messageToEdit, setMessageToEdit] = useState<ChatMessage | null>(null);
  const [threadMessageId, setThreadMessageId] = useState<ChatMessage['messageId'] | null>(null);

  const testId = anonymous ? 'anonymous-chat' : 'chat';
  const nickNameFormVisible = !chat.connected && !anonymous;
  const anonymousInfoVisible = !chat.connected && anonymous;
  const threadViewOpen = threadMessageId !== null;

  const saveEditedMessageData = (message: ChatMessage) => {
    setMessageToEdit(message);
    setMessageValue(message.message);
  };

  const clearMessageToEdit = () => {
    setMessageToEdit(null);
    setMessageValue('');
  };

  const handleSubmit = () => {
    if (messageToEdit) editMessage(messageToEdit.messageId, messageValue);
    else sendMessage(messageValue);
  };

  const sendMessage = async (message: string) => {
    if (await chat.sendMessage(message)) setMessageValue('');
  };

  const editMessage = async (messageId: number, message: string) => {
    if (await chat.editMessage(messageId, message)) clearMessageToEdit();
  };

  useEffect(() => {
    const threadMessageExists = chat.messages.some(({ messageId }) => messageId === threadMessageId);

    if (threadMessageId && !threadMessageExists) setThreadMessageId(null);
  }, [chat.messages, threadMessageId]);

  if (chat.providerError)
    return (
      <div data-testid={testId} {...props} className={classNames(styles.chat, styles.providerError, props.className)}>
        <ChatRemoveIcon /> {chat.providerError}
      </div>
    );

  if (nickNameFormVisible)
    return (
      <div data-testid={testId} {...props} className={classNames(styles.chat, props.className)}>
        <NicknameForm loading={chat.connecting} readOnly={hasSecurityPolicy} onSubmit={onNicknameSubmit} />
      </div>
    );

  if (anonymousInfoVisible) {
    return (
      <div
        data-testid={testId}
        className={classNames(styles.wrapper, styles.anonymousInfo, props.className)}
        {...props}
      >
        <div data-testid={`${testId}-info`}>
          <Heading>{t('viewer.views.stream.chat.anonymousInfo.unmoderated.heading')}</Heading>
          <SubHeading>{t('viewer.views.stream.chat.anonymousInfo.unmoderated.subHeading')}</SubHeading>
        </div>

        <PrimaryButton data-testid={`${testId}-join-button`} onClick={() => onNicknameSubmit('')}>
          {t('viewer.views.stream.chat.anonymousInfo.unmoderated.button')}
        </PrimaryButton>
      </div>
    );
  }

  return (
    <div data-testid={testId} {...props} className={classNames(styles.chat, props.className)}>
      <MessagesList
        messages={chat.messages}
        userId={chat.user?.userId}
        userIsModerator={chat.user?.userId === moderatorId}
        loading={chat.deletingMessage}
        readOnly={readOnly}
        anonymous={anonymous}
        onEditMessage={saveEditedMessageData}
        onDeleteMessage={chat.deleteMessage}
        onReactToMessage={chat.reactToMessage}
        onReplyToMessage={setThreadMessageId}
      />

      <ThreadView
        isOpen={threadViewOpen}
        messages={chat.messages}
        userId={chat.user?.userId}
        threadMessageId={threadMessageId}
        loading={chat.sendingMessage || chat.editingMessage}
        error={chat.userActionError}
        readOnly={readOnly}
        onReply={chat.sendThreadMessage}
        onEditMessage={chat.editMessage}
        onDeleteMessage={chat.deleteMessage}
        onReactToMessage={chat.reactToMessage}
        onQuitThread={() => setThreadMessageId(null)}
      />

      {!readOnly && (
        <MessageForm
          value={messageValue}
          onChange={setMessageValue}
          cancelButtonVisible={messageToEdit !== null}
          loading={chat.sendingMessage || chat.editingMessage}
          error={chat.userActionError}
          onCancel={clearMessageToEdit}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
}
